'use client';

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, TriangleAlert } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { apiKeysApi } from '@/lib/api/api-keys';
import { apiErrorMessage } from '@/lib/api/errors';
import type { ApiKeyCreateRequest, ApiKeyEnvironment } from '@/lib/api/types';
import {
  API_KEY_NAME_MAX_LENGTH,
  scopesForAccess,
  validateApiKeyName,
  type ApiKeyAccess,
} from '@/lib/api-keys';

interface CreateApiKeyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Two phases: a form, then a one-time reveal of the plaintext key. Once the
 * key is on screen the dialog ignores outside clicks and Escape and hides its
 * corner close button, so the secret cannot be lost by a stray click; only
 * the explicit acknowledgement closes it.
 */
export function CreateApiKeyDialog({ open, onOpenChange }: CreateApiKeyDialogProps) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  // Backend defaults: environment=live, scopes=[read, write].
  const [environment, setEnvironment] = useState<ApiKeyEnvironment>('live');
  const [access, setAccess] = useState<ApiKeyAccess>('read_write');
  const [formError, setFormError] = useState<string | null>(null);
  const [plaintext, setPlaintext] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const reset = () => {
    setName('');
    setEnvironment('live');
    setAccess('read_write');
    setFormError(null);
    setPlaintext(null);
    setCopied(false);
  };

  const mutation = useMutation({
    mutationFn: (payload: ApiKeyCreateRequest) => apiKeysApi.create(payload),
    // The result carries the plaintext key. Drop it from the MutationCache as
    // soon as this dialog unmounts (e.g. navigating away while the key is
    // shown) instead of keeping it for the default five minutes.
    gcTime: 0,
    onSuccess: (created) => {
      setPlaintext(created.key);
      void queryClient.invalidateQueries({ queryKey: ['api-keys'] });
    },
    onError: (error) => {
      setFormError(apiErrorMessage(error, 'The API key could not be created. Try again.'));
    },
  });

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setFormError(null);
    const nameError = validateApiKeyName(name);
    if (nameError) {
      setFormError(nameError);
      return;
    }
    mutation.mutate({ name: name.trim(), environment, scopes: scopesForAccess(access) });
  };

  const copyKey = async () => {
    if (!plaintext) return;
    try {
      await navigator.clipboard.writeText(plaintext);
      setCopied(true);
      toast.success('API key copied');
    } catch {
      toast.error('Could not copy. Select the key and copy it manually.');
    }
  };

  const close = () => {
    reset();
    mutation.reset();
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && plaintext) return;
        if (!next) {
          if (mutation.isPending) return;
          reset();
          mutation.reset();
        }
        onOpenChange(next);
      }}
    >
      <DialogContent
        className="sm:max-w-lg"
        showCloseButton={!plaintext}
        onInteractOutside={(event) => {
          if (plaintext) event.preventDefault();
        }}
        onEscapeKeyDown={(event) => {
          if (plaintext) event.preventDefault();
        }}
      >
        {plaintext ? (
          <>
            <DialogHeader>
              <DialogTitle>Copy your API key</DialogTitle>
              <DialogDescription>
                Store it in your secret manager or your integration&apos;s settings now.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <Label htmlFor="api-key-plaintext">API key</Label>
              <div className="flex gap-2">
                <Input
                  id="api-key-plaintext"
                  readOnly
                  value={plaintext}
                  onFocus={(event) => event.target.select()}
                  className="font-mono text-xs"
                />
                <Button type="button" variant="outline" onClick={copyKey}>
                  {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
                  {copied ? 'Copied' : 'Copy'}
                </Button>
              </div>
              <p className="flex items-start gap-2 text-sm text-destructive">
                <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                <span>
                  You won&apos;t see it again. rii.link stores only a hash, so a lost key can&apos;t be
                  recovered — revoke it and create a new one instead.
                </span>
              </p>
            </div>
            <DialogFooter>
              <Button type="button" onClick={close}>
                I&apos;ve saved it
              </Button>
            </DialogFooter>
          </>
        ) : (
          <form onSubmit={handleSubmit} className="grid gap-5">
            <DialogHeader>
              <DialogTitle>Create API key</DialogTitle>
              <DialogDescription>
                The key belongs to this workspace, not to your personal account, and keeps working if
                you leave the team. Revoke it when the integration no longer needs it.
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-2">
              <Label htmlFor="api-key-name">Name</Label>
              <Input
                id="api-key-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="e.g. Zapier production"
                maxLength={API_KEY_NAME_MAX_LENGTH}
                autoComplete="off"
              />
              <p className="text-xs text-muted-foreground">
                Pick something that identifies the integration. Workspace admins see it in the key list.
              </p>
            </div>

            <fieldset className="grid gap-3">
              <legend className="mb-3 text-sm font-medium">Environment</legend>
              <RadioGroup
                value={environment}
                onValueChange={(value) => setEnvironment(value as ApiKeyEnvironment)}
              >
                <ChoiceRow
                  id="api-key-env-live"
                  value="live"
                  label="Live"
                  hint="Creates real links that redirect traffic and count toward your plan."
                />
                <ChoiceRow
                  id="api-key-env-test"
                  value="test"
                  label="Test"
                  hint="Works in a separate sandbox. Links it creates never redirect real traffic and don't count toward your plan."
                />
              </RadioGroup>
            </fieldset>

            <fieldset className="grid gap-3">
              <legend className="mb-3 text-sm font-medium">Access</legend>
              <RadioGroup value={access} onValueChange={(value) => setAccess(value as ApiKeyAccess)}>
                <ChoiceRow
                  id="api-key-access-read-write"
                  value="read_write"
                  label="Read and write"
                  hint="Everything read-only can, plus create, update and delete links, add and remove domains, and create QR codes."
                />
                <ChoiceRow
                  id="api-key-access-read"
                  value="read"
                  label="Read only"
                  hint="Read the links, domains and QR codes created through the API, plus per-link click stats. Cannot change anything."
                />
              </RadioGroup>
            </fieldset>

            {formError && (
              <p role="alert" className="text-sm text-destructive">
                {formError}
              </p>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={close}
                disabled={mutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending ? 'Creating…' : 'Create key'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ChoiceRow({
  id,
  value,
  label,
  hint,
}: {
  id: string;
  value: string;
  label: string;
  hint: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <RadioGroupItem id={id} value={value} className="mt-0.5" aria-describedby={`${id}-hint`} />
      <div className="grid gap-1">
        <Label htmlFor={id} className="font-medium">
          {label}
        </Label>
        <p id={`${id}-hint`} className="text-xs text-muted-foreground">
          {hint}
        </p>
      </div>
    </div>
  );
}
